// Избранное аккаунта (общие с футболом ручки /account/favorites/). Вид спорта сервер берёт из токена ПРИЛОЖЕНИЯ:
// entity_id в теле — голое число, префикс «cricket:» сервер хранит сам. Ответы POST/DELETE — полный авторитетный
// набор: рисуем ЕГО, не оптимистичную догадку. Набор читаем, только если сессия уже есть (гость — лениво).
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, apiRequest } from '../api/client';
import { currentScreen, setUserProperty, track } from '../analytics/track';
import { hasSession, onSessionChange, upgradeGuestToTelegram } from './session';

export type FavType = 'team' | 'league' | 'player' | 'match';
export const FAV_TYPES: FavType[] = ['team', 'league', 'player', 'match'];
type Row = { entity_type: string; entity_id: number };
export type FavoritesSet = Record<FavType, number[]>;
export const EMPTY: FavoritesSet = { team: [], league: [], player: [], match: [] };

const PATH = '/api/v5/mobile/account/favorites/';
const KEY = ['account-favorites'];

/** Набор с сервера (загрузка, ответ POST/DELETE) → свойство пользователя favorite_teams_count (число строкой). */
function noteSet(set: FavoritesSet): FavoritesSet {
  setUserProperty('favorite_teams_count', String(set.team.length));
  return set;
}

/** Ответ сервера (сгруппирован по типам; лишние ключи вроде country игнорируем) → наш набор. */
export function toSet(response: Partial<Record<string, Row[]>>): FavoritesSet {
  const out: FavoritesSet = { team: [], league: [], player: [], match: [] };
  for (const type of FAV_TYPES) out[type] = (response[type] ?? []).map((r) => Number(r.entity_id)).filter(Number.isFinite);
  return out;
}

export function useHasSession(): boolean {
  const [has, setHas] = useState(hasSession);
  useEffect(() => onSessionChange(() => setHas(hasSession())), []);
  return has;
}

/** Набор аккаунта (GET) — и favorite_teams_count. */
export async function loadFavorites(signal?: AbortSignal): Promise<FavoritesSet> {
  return noteSet(toSet(await apiRequest<Record<string, Row[]>>('GET', PATH, { auth: true, signal })));
}

export function useFavorites(): FavoritesSet {
  return useFavoritesState().set;
}

/** Запрос набора, которого ждут ворота онбординга: повторить (Retry) и признаки для Loading (States). */
export interface FavoritesQuery { failureCount: number; failureReason: unknown; fetchStatus?: string; refetch: () => Promise<unknown> }
export interface FavoritesState {
  set: FavoritesSet; loaded: boolean; session: boolean; failed: boolean;
  /** запрос стоит на паузе без сети (fetchStatus 'paused'): ответа не будет, пока сеть не вернётся (HQ-1797) */
  paused: boolean;
  query: FavoritesQuery;
}

/** Состояние запроса TanStack → состояние набора (чистая функция; хук ниже и тест HQ-1797). */
export function favoritesStateOf(
  query: FavoritesQuery & { data: FavoritesSet | undefined; isError: boolean }, session: boolean,
): FavoritesState {
  return {
    set: query.data ?? EMPTY, loaded: query.data !== undefined, session,
    failed: query.isError && query.data === undefined,
    paused: query.fetchStatus === 'paused' && query.data === undefined,
    query,
  };
}

/** Набор и то, пришёл ли он с сервера: «пусто» до ответа ≠ «пусто у человека» (мягкая карточка выбора, HQ-726).
 *  failed — набор не пришёл и уже не придёт без повтора (онбординг HQ-1092 не ждёт его вечно). */
export function useFavoritesState(): FavoritesState {
  const has = useHasSession();
  const query = useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) => loadFavorites(signal),
    enabled: has,
    staleTime: 60_000,
  });
  return favoritesStateOf(query, has);
}

/** Всего избранного во всех типах. */
export const favoritesCount = (set: FavoritesSet): number => FAV_TYPES.reduce((n, type) => n + set[type].length, 0);

export class FavoriteLimitError extends Error {
  constructor(readonly type: FavType, readonly limit: number) { super('favorite_limit_exceeded'); }
}

/** Поставить / снять звезду (on — уже горит). Успех — favorite_add / favorite_remove {entity_type, entity_id, source}
 *  (a_account_favorites* не уходит: events.SKIP) и favorite_teams_count из авторитетного ответа. */
export async function toggleFavorite({ type, id, on }: { type: FavType; id: number; on: boolean }): Promise<FavoritesSet> {
  // экран, где нажали звезду, — в момент нажатия (ответ может прийти уже на другом)
  const source = currentScreen();
  try {
    const response = on
      ? await apiRequest<Record<string, Row[]>>('DELETE', `${PATH}${type}/${id}/`, { auth: true })
      : await apiRequest<Record<string, Row[]>>('POST', PATH, { auth: true, body: { entity_type: type, entity_id: id } });
    track(on ? 'favorite_remove' : 'favorite_add', { entity_type: type, entity_id: String(id), source });
    return noteSet(toSet(response));
  } catch (e) {
    if (e instanceof ApiError && e.code === 'favorite_limit_exceeded') {
      throw new FavoriteLimitError(type, Number(e.payload?.limit ?? 0));
    }
    throw e;
  }
}

/** Мутация звезды. Ответ — авторитетный набор ЦЕЛИКОМ, поэтому тапы по разным звёздам идут строго по очереди
 *  (одна scope: react-query запускает следующую после завершения предыдущей, в т. ч. после ошибки): иначе
 *  опоздавший ответ первого тапа {A} затирал бы ответ второго {A,B} (HQ-1395). По той же причине мутация отменяет
 *  идущее чтение набора: GET, начатый до тапа, иначе пришёл бы позже со старым набором и погасил звезду (HQ-1719). */
export function favoriteMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: toggleFavorite,
    scope: { id: 'account-favorites' },
    onMutate: async () => { await queryClient.cancelQueries({ queryKey: KEY }); },
    onSuccess: (set: FavoritesSet) => { queryClient.setQueryData(KEY, set); },
  };
}

export function useToggleFavorite() {
  return useMutation(favoriteMutationOptions(useQueryClient()));
}

/** Старт приложения: если уже были гостем, а Telegram-вход появился — апгрейд (избранное сольётся на сервере). */
export async function bootstrapSession(): Promise<void> {
  if (!hasSession()) return;
  await upgradeGuestToTelegram();
}

/** Параметры набора для полки и хаба: все четыре ключа ВСЕГДА (пустой перекрывает серверный), id без дублей по порядку. */
export function favoriteParams(set: FavoritesSet): Record<'teams' | 'leagues' | 'players' | 'matches', string> {
  const join = (ids: number[]) => [...new Set(ids)].sort((a, b) => a - b).join(',');
  return { teams: join(set.team), leagues: join(set.league), players: join(set.player), matches: join(set.match) };
}
